import { useMemo } from 'react';
import { useIntl } from 'umi';
import { BROWSER_LANGUAGE } from '@gp/constants/const';
import { LANGUAGE_EN } from '@home/_config/config';
import { unitConversion } from '@/pages/datalab/utils/utils';
import type { ChartOption } from '@customComp/Chart/_config/_interface';
import { getSeriesAndLegend } from '@customComp/Chart/chartConfigs/seriesUtils';
import {
  getTooltipConfig,
} from '@customComp/Chart/chartConfigs/tooltip';
import { getLegendConfig } from '@customComp/Chart/chartConfigs/legend/legendUtils';
import type { IBarChartOptionProps } from '@customComp/Chart/chartConfigs/types';

export const useStackBarOption = (props: IBarChartOptionProps): ChartOption => {
  const {
    xAxisData,
    seriesData,
    metricIsAllPercent,
    metricNames,
    // isOnlyOneDimension,
    fieldInfos,
    setOtherConfigFunc,
    chartConfig,
    tooltipMinTopPx,
  } = props;
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  
  // 单独计算 option其他固定配置
  const option = useMemo<ChartOption>(() => {
    const cfg = chartConfig as { tooltip?: Record<string, unknown> } | undefined;
    const tooltipRest = cfg?.tooltip && typeof cfg.tooltip === 'object' ? cfg.tooltip : {};
    const displayConfig: Record<string, {accuracy: number; unit: string;}> = {};
    metricNames.forEach((metricName: string) => {
      const {accuracy, unit} = fieldInfos?.find((fieldInfo) => fieldInfo.name === metricName) || {accuracy: 0, unit: 'bmk'};
      displayConfig[metricName] = {accuracy, unit};
    });
    const {series, legendData} = getSeriesAndLegend({
      seriesData,
      displayConfig,
      setSeriesItem: (seriesItem) => {
        // TODO: 当前还没有对比组, 以后添加对比组的时候, 取消注释即可
        // 按对比组分堆叠：
        // - 当前新结构只有“基准 + 对比”两组，通过 isCompare 拆成两个堆叠组
        // const stackGroupIndex = isCompare ? 1 : 0;
        return {
          ...seriesItem,
          type: 'bar',
          barMaxWidth: 80,
          barCategoryGap: '50%',
          itemStyle: {
            ...seriesItem?.itemStyle,
          },
          // stack: `total${stackGroupIndex}`,
          stack: 'total',
        };
      },
      isEn,
      legendColors: chartConfig?.legendColors,
    });
    
    const baseOption: ChartOption = {
      grid: {
        left: BROWSER_LANGUAGE?.startsWith('en-') ? 6 : 4,
        top: 20,
        right: 4,
        bottom: 25,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        axisTick: {
          alignWithLabel: true,
        },
        axisLabel: {
          // 不用写入textStyle对象里面，会echart报黄色提示
          color: '#838697',
          fontSize: 10,
          align: 'center',
        },
        axisLine: {
          lineStyle: {
            color: '#D8DAE4',
          },
          show: true,
        },
        data: xAxisData ?? [],
      },
      tooltip: getTooltipConfig({
        ...tooltipRest,
        tooltipMinTopPx,
      }),
      yAxis: [
        {
          type: 'value',
          splitLine: {
            // 分割线效果
            show: true,
            lineStyle: {
              type: 'dashed',
              color: '#ddd',
            },
          },
          // splitNumber: 5,
          axisLabel: {
            color: '#838697',
            fontSize: 12,
            formatter(value: number | string) {
              return unitConversion(value, true);
            },
          },
        },
      ],
      legend: getLegendConfig({data: legendData}),
      series: series ?? []
    };
    
    // 如果 多个指标中, 全是百分比类型, 则Y轴展示百分比
    if (metricIsAllPercent) {
      baseOption.yAxis[0].axisLabel.formatter = '{value}%';
    }
    
    return baseOption;
    
  }, [xAxisData, seriesData, isEn, fieldInfos, chartConfig?.legendColors, tooltipMinTopPx]);
  
  // 最后结合得到 最终的option, 避免图表宽高连续变化时, 多次重新计算option的固定配置
  return useMemo(() =>{
    return setOtherConfigFunc ? setOtherConfigFunc(option) : option;
  }, [option, setOtherConfigFunc]);
};